import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that displays store stockists for a specific variant's barcode.
 * It cross-references product distribution metafields against the master store list.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} listOutput - The container to render the store list into
 *
 * @extends {Component<Refs>}
 */
class ProductStockistsComponent extends Component {
  requiredRefs = ['listOutput'];

  connectedCallback() {
    super.connectedCallback();
    this.updateStockists(this.dataset.initialBarcode);
    
    // Listen on document because the component might be an independent section
    document.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
  }

  /** @param {VariantUpdateEvent} e */
  handleVariantUpdate = (e) => {
    const currentProductId = String(this.dataset.productId);
    const eventProductId = String(e.detail?.data?.productId || '');

    // Only update if it's for the correct product
    if (eventProductId === currentProductId) {
      const barcode = e.detail?.resource?.barcode;
      this.updateStockists(barcode);
    }
  };

  static #masterStoresCache = null;

  /**
   * Updates the stockists list based on the provided barcode.
   * @param {string} [barcode] - The variant barcode to look up
   */
  updateStockists = async (barcode) => {
    // 1. Resolve barcode
    if (!barcode) {
      try {
        barcode = JSON.parse(this.dataset.initialBarcode || '""');
      } catch (e) {
        barcode = this.dataset.initialBarcode;
      }
    }

    if (!barcode) {
      this.style.display = 'none';
      return;
    }

    // 2. Load distribution data and master store list (with caching)
    let distribution = [];
    try {
      distribution = JSON.parse(this.dataset.distributionJson || '[]');
    } catch(e) { console.error('Failed to parse distribution JSON', e); }

    if (!ProductStockistsComponent.#masterStoresCache) {
      const url = this.dataset.masterStoresUrl;
      if (url) {
        try {
          const response = await fetch(url);
          ProductStockistsComponent.#masterStoresCache = await response.json();
        } catch(e) {
          console.error('Failed to fetch master store list', e);
        }
      }
    }

    const masterStores = ProductStockistsComponent.#masterStoresCache || [];

    // 3. Filter distribution for our barcode
    const activeNamesSet = new Set();
    distribution
      .filter(item => String(item.barcode) === String(barcode))
      .forEach(item => {
        // Split by newlines and trim each name
        const names = String(item.store_name || '').split('\n');
        names.forEach(n => {
          if (n.trim()) activeNamesSet.add(n.trim());
        });
      });

    if (activeNamesSet.size === 0) {
      this.style.display = 'none';
      return;
    }

    // Find matching branches (check both short ID and full store name)
    const activeNames = Array.from(activeNamesSet);
    const matchingBranches = masterStores.filter(store => {
      const shortIdMatch = activeNames.includes(String(store.name).trim());
      const fullNameMatch = activeNames.includes(String(store.store_name).trim());
      return shortIdMatch || fullNameMatch;
    });

    if (matchingBranches.length === 0) {
      this.style.display = 'none';
      return;
    }

    // 4. Build and Render HTML
    this.style.display = 'block';
    
    let html = '';
    matchingBranches.forEach((store) => {
      html += `
        <div class="stockist-item" style="padding: 0.75rem 0; border-top: 1px solid var(--color-border); flex: 1 1 300px; max-width: 100%;">
          <div style="display: flex; flex-direction: column; gap: 0.25rem;">
            <p style="font-weight: 500; margin: 0; color: var(--color-foreground);">
              ${store.store_name}
            </p>
            <p style="font-size: 0.875rem; color: var(--color-foreground-secondary); margin: 0;">
              ${store.address}
            </p>
            ${store.telephone ? `<p style="font-size: 0.875rem; margin: 0; color: var(--color-foreground-secondary);">Tel: ${store.telephone}</p>` : ''}
            <a href="${store.map_url}" target="_blank" rel="noopener" 
               style="font-size: 0.75rem; text-decoration: underline; color: var(--color-primary); margin-top: 0.25rem; width: fit-content;">
               View on Google Map
            </a>
          </div>
        </div>
      `;
    });

    if (this.refs.listOutput) {
      this.refs.listOutput.innerHTML = html;
    }
  };
}

if (!customElements.get('product-stockists-component')) {
  customElements.define('product-stockists-component', ProductStockistsComponent);
}
